import { typographyTokens } from '../../../../theme/typographyTokens';
/**
 * SEPDetailsTable Component
 * 
 * Displays a table of selected SEPs with view and edit actions.
 * Manages modal states for SEPViewModal and SEPEditModal.
 * 
 * @module SEPDetailsTable
 * @path src/frontend/features/Admin/Others/SEPDetails/SEPDetailsTable.tsx
 */
import React, { useState, useCallback, useMemo } from 'react';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SEPViewModal, { SEPDetailData } from './SEPViewModal';
import SEPEditModal, { SEPEditFormData } from './SEPEditModal';
import { useUpdateSepMutation } from '../../../../../frontend/services/sepSlice';

/**
 * Props interface for SEPDetailsTable
 * @property {any[]} selectedSeps - Array of SEP objects to display
 * @property {function} onEdit - Optional callback when edit action is triggered
 * @property {any} theme - MUI theme object for styling
 */
interface SEPDetailsTableProps {
  selectedSeps: any[];
  onEdit?: (sep: any) => void;
  theme: any;
}

/**
 * SEPDetailsTable - Renders SEP data in a table format with action buttons
 * 
 * Features:
 * - Displays SEP ID, Name, Phase, Status, Workflow Type, Dates
 * - View action opens SEPViewModal (read-only)
 * - Edit action opens SEPEditModal (editable form)
 * - Theme-aware styling (light/dark mode support)
 * 
 * @param {SEPDetailsTableProps} props - Component props
 * @returns {JSX.Element} The SEP details table with modals
 */
const SEPDetailsTable: React.FC<SEPDetailsTableProps> = ({ selectedSeps, onEdit, theme }) => {
  // Modal visibility states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  
  // Selected SEP data for modals
  const [selectedSepForView, setSelectedSepForView] = useState<SEPDetailData | null>(null);
  const [selectedSepForEdit, setSelectedSepForEdit] = useState<SEPEditFormData | null>(null);
  
  // RTK Query mutation hook for updating SEPs
  const [updateSep] = useUpdateSepMutation();

  /**
   * Opens the edit modal with selected SEP data
   * Extracts only the editable fields for the form
   * @param {any} sep - The SEP object to edit
   */
  const handleEdit = useCallback((sep: any) => {
    setSelectedSepForEdit({
      id: sep.id,
      name: sep.name,
      description: sep.description,
      completedAt: sep.completedAt,
      validationDate: sep.validationDate,
      phase: sep.phase,
      status: sep.status,
      startedAt: sep.startedAt,
    });
    setEditModalOpen(true);
    // Optional callback for parent component
    if (onEdit) {
      onEdit(sep);
    }
  }, [onEdit]);

  /**
   * Opens the view modal with full SEP data
   * @param {any} sep - The SEP object to view
   */
  const handleView = useCallback((sep: any) => {
    setSelectedSepForView(sep);
    setViewModalOpen(true);
  }, []);

  /**
   * Closes the view modal and clears selected data
   */
  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedSepForView(null);
  }, []);

  /**
   * Closes the edit modal and clears selected data
   */
  const handleCloseEditModal = useCallback(() => {
    setEditModalOpen(false);
    setSelectedSepForEdit(null);
  }, []);

  /**
   * Formats a date string for display in the table
   * @param {string | null} dateString - ISO date string or null
   * @returns {string} Formatted date or '-' if null
   */
  const formatDate = useCallback((dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  }, []);

  /**
   * Memoized table header styles based on theme mode
   */
  const headerRowSx = useMemo(() => ({
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.common.black
      : theme.palette.gray.lightGray4,
    '& th': {
      color: theme.palette.mode === 'dark'
        ? theme.palette.common.white
        : theme.palette.common.black
    }
  }), [theme.palette]);

  /**
   * Memoized table row hover styles based on theme mode
   */
  const getRowHoverSx = useMemo(() => ({
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.gray.darkGray1
        : theme.palette.gray.lightGray2,
    }
  }), [theme.palette]);

  return (
    <>
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
        <Typography sx={{ fontWeight: typographyTokens.fontWeights.bold, fontSize: typographyTokens.fontSizes.xl, mb: 2 }}>
          SEP Details ({selectedSeps.length})
        </Typography>
        <TableContainer>
          <Table size="small">
            {/* Table Header - Column definitions */}
            <TableHead>
              <TableRow sx={headerRowSx}>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '8%' }}>SEP ID</TableCell>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '18%' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '10%' }}>Phase</TableCell>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '10%' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '14%' }}>Workflow Type</TableCell>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '12%' }}>Validation Date</TableCell>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '12%' }}>Completed Date</TableCell>
                <TableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold, width: '8%', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            {/* Table Body - SEP data rows */}
            <TableBody>
              {selectedSeps.map((sep) => (
                <TableRow
                  key={sep.id}
                  sx={getRowHoverSx}
                >
                  <TableCell>{sep.id}</TableCell>
                  <TableCell>{sep.name}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{sep.phase || '-'}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{sep.status || '-'}</TableCell>
                  <TableCell>{sep.workFlowType || '-'}</TableCell>
                  <TableCell>{formatDate(sep.validationDate)}</TableCell>
                  <TableCell>{formatDate(sep.completedAt)}</TableCell>
                  <TableCell sx={{ textAlign: 'center' }}>
                    <Tooltip title="View SEP Details">
                      <IconButton
                        size="small"
                        onClick={() => handleView(sep)}
                        sx={{ color: theme.palette.primary.main, mr: 1 }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit SEP Details">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(sep)}
                        sx={{ color: theme.palette.primary.main }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View Modal - Read-only SEP details */}
      <SEPViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        sepData={selectedSepForView}
        theme={theme}
      />

      {/* Edit Modal - Editable SEP form */}
      <SEPEditModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        sepData={selectedSepForEdit}
        updateSep={updateSep}
        theme={theme}
      />
    </>
  );
};

export default SEPDetailsTable;
